import React, { useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from '@mui/material';
import { secondsToTime, timeToSeconds } from '../../../utils/formatUtils';

const StyledTableContainer = styled(TableContainer)`
    height: 100%;
    background-color: #1e1e1e !important;
    
    .MuiTableCell-root {
        color: white;
        border-color: rgba(255, 255, 255, 0.1);
    }
    
    .MuiTableHead-root .MuiTableCell-root {
        background-color: #2c2c2c;
        font-weight: bold;
    }
    
    .time-cell {
        white-space: nowrap;
        text-align: center;
        min-width: 60px; /* Further reduced from 70px */
        padding: 4px 2px; /* Reduced padding even more */
        font-size: 0.7rem; /* Smaller font size */
        display: flex;
        flex-direction: column;
        align-items: center;
        line-height: 1.2; /* Tighter line height */
    }

    .time-cell .time-start {
        border-bottom: 1px solid rgba(255, 255, 255, 0.2);
        padding-bottom: 1px; /* Reduced padding */
        width: 100%;
        opacity: 0.9;
    }

    .time-cell .time-end {
        padding-top: 1px; /* Reduced padding */
        width: 100%;
        opacity: 0.9;
    }
    
    .text-cell {
        font-family: 'Roboto Mono', monospace;
        white-space: pre-wrap;
    }
    
    /* Improved styling for current subtitle cells */
    .current-cell {
        background-color: rgba(70, 130, 180, 0.5) !important;
        position: relative;
        font-weight: bold;
        border-left: 3px solid #4c9aff;
    }
    
    /* Playback indicator */
    .current-cell::before {
        content: '▶';
        position: absolute;
        left: 5px;
        color: #4c9aff;
        font-size: 10px;
    }
    
    /* Make sure entire row is highlighted */
    .MuiTableRow-root:has(.current-cell) {
        background-color: rgba(70, 130, 180, 0.2);
    }
    
    .row:hover {
        background-color: rgba(255, 255, 255, 0.05);
        cursor: pointer;
    }
    
    .column-group {
        border-right: 2px solid rgba(255, 255, 255, 0.2);
    }
    
    .column-group:last-child {
        border-right: none;
    }
    
    /* Animation for current cell */
    @keyframes pulse {
        0% { box-shadow: 0 0 0 0 rgba(76, 154, 255, 0.5); }
        70% { box-shadow: 0 0 0 5px rgba(76, 154, 255, 0); }
        100% { box-shadow: 0 0 0 0 rgba(76, 154, 255, 0); }
    }
    
    .current-cell {
        animation: pulse 2s infinite;
    }

/* Define colors for each column (up to 5 columns) */
.column-1 .current-cell {
    background-color: rgba(76, 175, 80, 0.3) !important; /* Green */
    border-left: 3px solid #4caf50;
}

.column-2 .current-cell {
    background-color: rgba(33, 150, 243, 0.3) !important; /* Blue */
    border-left: 3px solid #2196f3;
}

.column-3 .current-cell {
    background-color: rgba(255, 152, 0, 0.3) !important; /* Orange */
    border-left: 3px solid #ff9800;
}

.column-4 .current-cell {
    background-color: rgba(156, 39, 176, 0.3) !important; /* Purple */
    border-left: 3px solid #9c27b0;
}

.column-5 .current-cell {
    background-color: rgba(233, 30, 99, 0.3) !important; /* Pink */
    border-left: 3px solid #e91e63;
}

/* Color-specific playback indicators */
.column-1 .current-cell::before {
    color: #4caf50;
}

.column-2 .current-cell::before {
    color: #2196f3;
}

.column-3 .current-cell::before {
    color: #ff9800;
}

.column-4 .current-cell::before {
    color: #9c27b0;
}

.column-5 .current-cell::before {
    color: #e91e63;
}

/* Original current-cell styling becomes a fallback */
.current-cell {
    position: relative;
    font-weight: bold;
}

/* Column-specific header styling */
.MuiTableHead-root .column-1 {
    border-bottom: 2px solid #4caf50;
}

.MuiTableHead-root .column-2 {
    border-bottom: 2px solid #2196f3;
}

.MuiTableHead-root .column-3 {
    border-bottom: 2px solid #ff9800;
}

.MuiTableHead-root .column-4 {
    border-bottom: 2px solid #9c27b0;
}

.MuiTableHead-root .column-5 {
    border-bottom: 2px solid #e91e63;
}

/* Update column styling to include colored borders */
.column-1 {
    background-color: rgba(76, 175, 80, 0.03);
    border-left: 1px solid rgba(76, 175, 80, 0.3);
    border-right: 1px solid rgba(76, 175, 80, 0.3);
}

.column-2 {
    background-color: rgba(33, 150, 243, 0.03);
    border-left: 1px solid rgba(33, 150, 243, 0.3);
    border-right: 1px solid rgba(33, 150, 243, 0.3);
}

.column-3 {
    background-color: rgba(255, 152, 0, 0.03);
    border-left: 1px solid rgba(255, 152, 0, 0.3);
    border-right: 1px solid rgba(255, 152, 0, 0.3);
}

.column-4 {
    background-color: rgba(156, 39, 176, 0.03);
    border-left: 1px solid rgba(156, 39, 176, 0.3);
    border-right: 1px solid rgba(156, 39, 176, 0.3);
}

.column-5 {
    background-color: rgba(233, 30, 99, 0.03);
    border-left: 1px solid rgba(233, 30, 99, 0.3);
    border-right: 1px solid rgba(233, 30, 99, 0.3);
}

/* Enhanced header styling */
.MuiTableHead-root .column-1 {
    border-bottom: 2px solid #4caf50;
    background-color: rgba(76, 175, 80, 0.15);
    border-left: 1px solid rgba(76, 175, 80, 0.5);
    border-right: 1px solid rgba(76, 175, 80, 0.5);
    border-top: 2px solid #4caf50;
}

.MuiTableHead-root .column-2 {
    border-bottom: 2px solid #2196f3;
    background-color: rgba(33, 150, 243, 0.15);
    border-left: 1px solid rgba(33, 150, 243, 0.5);
    border-right: 1px solid rgba(33, 150, 243, 0.5);
    border-top: 2px solid #2196f3;
}

.MuiTableHead-root .column-3 {
    border-bottom: 2px solid #ff9800;
    background-color: rgba(255, 152, 0, 0.15);
    border-left: 1px solid rgba(255, 152, 0, 0.5);
    border-right: 1px solid rgba(255, 152, 0, 0.5);
    border-top: 2px solid #ff9800;
}

.MuiTableHead-root .column-4 {
    border-bottom: 2px solid #9c27b0;
    background-color: rgba(156, 39, 176, 0.15);
    border-left: 1px solid rgba(156, 39, 176, 0.5);
    border-right: 1px solid rgba(156, 39, 176, 0.5);
    border-top: 2px solid #9c27b0;
}

.MuiTableHead-root .column-5 {
    border-bottom: 2px solid #e91e63;
    background-color: rgba(233, 30, 99, 0.15);
    border-left: 1px solid rgba(233, 30, 99, 0.5);
    border-right: 1px solid rgba(233, 30, 99, 0.5);
    border-top: 2px solid #e91e63;
}

/* Colored header text */
.MuiTableHead-root .column-1 {
    color: #4caf50;
    font-weight: bold;
}

.MuiTableHead-root .column-2 {
    color: #2196f3;
    font-weight: bold;
}

.MuiTableHead-root .column-3 {
    color: #ff9800;
    font-weight: bold;
}

.MuiTableHead-root .column-4 {
    color: #9c27b0;
    font-weight: bold;
}

.MuiTableHead-root .column-5 {
    color: #e91e63;
    font-weight: bold;
}
`;

export default function MultiColumnSubtitleTable({
    columns,
    currentIndex,
    currentTime,
    player,
    handleSetTime
}) {
    const checkSub = useCallback((sub) => {
        if (!sub) return false;
        
        // Check if it's a valid subtitle object with required properties
        return (
            sub &&
            typeof sub === 'object' &&
            'startTime' in sub &&
            'endTime' in sub &&
            'text2' in sub
        );
    }, []);
    
    // Get the maximum number of subtitles across all columns
    const maxRows = useMemo(() => {
        return columns.reduce((max, col) => {
            if (col.language && col.subtitles && col.subtitles.length > max) {
                return col.subtitles.length;
            }
            return max;
        }, 0);
    }, [columns]);
    
    const handleRowClick = (colIndex, startTime) => {
        if (player && player.element) {
            const seekTime = Math.max(0, startTime);
            handleSetTime(seekTime);
        }
    };
    
    // Function to track current subtitles in each column
    const currentSubtitleIndexes = useMemo(() => {
        return columns.map(col => {
            if (!col.language || !col.subtitles) return -1;
            
            return col.subtitles.findIndex(
                sub => sub && currentTime >= sub.startTime && currentTime <= sub.endTime
            );
        });
    }, [columns, currentTime]);
    
    // Auto-scroll to current subtitle
    React.useEffect(() => {
        const activeElements = document.querySelectorAll('.current-cell');
        if (activeElements.length > 0) {
            // Find the first visible active element
            const activeElement = Array.from(activeElements).find(el => {
                const rect = el.getBoundingClientRect();
                return rect.height > 0 && rect.width > 0;
            });
            
            if (activeElement) {
                activeElement.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center'
                });
            }
        }
    }, [currentSubtitleIndexes]);
    
    return (
        <StyledTableContainer component={Paper}>
            <Table stickyHeader size="small">
                <TableHead>
                    <TableRow>
                        {columns.map((col, colIndex) => {
                            if (!col.language) return null;
                            const columnClass = `column-${colIndex + 1}`;
                            const columnColor = colIndex === 0 ? '#4caf50' : 
                                               colIndex === 1 ? '#2196f3' : 
                                               colIndex === 2 ? '#ff9800' : 
                                               colIndex === 3 ? '#9c27b0' : 
                                               colIndex === 4 ? '#e91e63' : '#4c9aff';
                            
                            return (
                                <React.Fragment key={`header-${colIndex}`}>
                                    <TableCell 
                                        className={`time-cell ${columnClass}`}
                                        style={{ 
                                            borderTop: `2px solid ${columnColor}`,
                                            borderLeft: `1px solid ${columnColor}` 
                                        }}
                                    >
                                        <span style={{ 
                                            fontSize: '0.7rem',
                                            color: columnColor,
                                            fontWeight: 'bold'
                                        }}>
                                            Time
                                        </span>
                                        {currentSubtitleIndexes[colIndex] >= 0 && (
                                            <span style={{ 
                                                marginLeft: '2px', 
                                                color: columnColor,
                                                fontSize: '10px'
                                            }}>
                                                ▶
                                            </span>
                                        )}
                                    </TableCell>
                                    <TableCell 
                                        className={columnClass}
                                        style={{ 
                                            borderTop: `2px solid ${columnColor}`,
                                            borderRight: `1px solid ${columnColor}` 
                                        }}
                                    >
                                        <span style={{ color: columnColor, fontWeight: 'bold' }}>
                                            {col.language} {col.version !== 'latest' && 
                                                `(Version ${col.versions.find(v => v.id === col.version)?.versionNumber || ''})`}
                                        </span>
                                    </TableCell>
                                </React.Fragment>
                            );
                        })}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {Array.from({ length: maxRows }).map((_, rowIndex) => (
                        <TableRow 
                            key={`row-${rowIndex}`}
                            className={columns.some((col, colIndex) => 
                                col.language && 
                                currentSubtitleIndexes[colIndex] === rowIndex
                            ) ? 'current-row' : ''}
                        >
                            {columns.map((col, colIndex) => {
                                if (!col.language) return null;
                                
                                const subtitle = col.subtitles[rowIndex];
                                const isCurrentSub = subtitle && 
                                    currentTime >= subtitle.startTime && 
                                    currentTime <= subtitle.endTime;
                                
                                // Add column-specific class
                                const columnClass = `column-${colIndex + 1}`;
                                
                                return (
                                    <React.Fragment key={`cell-${colIndex}-${rowIndex}`}>
                                        <TableCell 
                                            className={`time-cell ${columnClass} ${isCurrentSub ? 'current-cell' : ''}`}
                                            onClick={() => subtitle && handleRowClick(colIndex, subtitle.startTime)}
                                        >
                                            {subtitle ? 
                                                <React.Fragment>
                                                    <div className="time-start">{secondsToTime(subtitle.startTime, true)}</div>
                                                    <div className="time-end">{secondsToTime(subtitle.endTime, true)}</div>
                                                </React.Fragment> : 
                                                ''}
                                        </TableCell>
                                        <TableCell 
                                            className={`text-cell ${columnClass} ${isCurrentSub ? 'current-cell' : ''}`}
                                            onClick={() => subtitle && handleRowClick(colIndex, subtitle.startTime)}
                                            id={isCurrentSub ? `active-sub-${colIndex}` : undefined}
                                        >
                                            {subtitle ? subtitle.text2 : ''}
                                        </TableCell>
                                    </React.Fragment>
                                );
                            })}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </StyledTableContainer>
    );
}